import type {
  CloudAuditQueryRow,
  SessionViewSource,
} from "@p0security/shared/integrations/sessions/types";
import { Button, Checkbox, Empty, Flex, Table, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import type { ColumnType } from "antd/es/table";
import { FormattedDateTime } from "components/common/FormattedDateTime";
import React, { useCallback, useMemo, useState } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

import { getUserIdentity, parseUserAgent } from "../sessionUtils";
import { JsonViewerModal } from "./JsonViewerModal";

const formatUserAgent = (data?: string) => {
  if (!data) {
    return null;
  }

  const parsed = parseUserAgent(data);
  return parsed.type === "browser"
    ? parsed.type
    : parsed.type === "aws-cli"
      ? `aws-cli/${parsed.cliVersion} ${
          parsed.meta.cfg ? `| ${parsed.meta.cfg}` : ""
        } ${parsed.meta.md ? `| ${parsed.meta.md}` : ""}`
      : parsed.products.map((product) => product.name).join(", ");
};

const getCloudAuditIdentity = (principal: unknown): string => {
  return typeof principal === "string" ? principal : getUserIdentity(principal);
};

const parseCloudAuditTimestamp = (value: string): number => {
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
  return new Date(normalized).getTime();
};

const statusTagColor = (
  value: string,
  source?: SessionViewSource
): { bg: string; text: string } | undefined => {
  if (source === "gcp-cloud-audit") {
    const severity = value.toLowerCase();
    if (
      severity === "error" ||
      severity === "critical" ||
      severity === "alert"
    ) {
      return colors.tagColors.red;
    }
    if (severity === "warning" || severity === "notice") {
      return colors.tagColors.yellow;
    }
    return undefined;
  }

  return colors.tagColors.red;
};

const RawLogButton: React.FC<{
  data: CloudAuditQueryRow;
  onExpandJson: (data: unknown, title: string) => void;
}> = ({ data, onExpandJson }) => {
  const handleClick = useCallback(() => {
    onExpandJson(data.raw ?? data, "Raw Log");
  }, [data, onExpandJson]);

  return (
    <Button type="link" onClick={handleClick}>
      view
    </Button>
  );
};

export const CloudAuditRowsTable: React.FC<{
  rows: CloudAuditQueryRow[];
  source?: SessionViewSource;
  update: React.ReactNode;
}> = ({ rows, source, update }) => {
  const [showStatus, setShowStatus] = useState(true);
  const [expandedJson, setExpandedJson] = useState<{
    data: unknown;
    title: string;
  } | null>(null);

  const handleExpandJson = useCallback((data: unknown, title: string) => {
    setExpandedJson({ data, title });
  }, []);

  const columns = useMemo(() => {
    const nextColumns: ColumnType<CloudAuditQueryRow>[] = [
      {
        title: "Raw Log",
        render: (data) => (
          <RawLogButton data={data} onExpandJson={handleExpandJson} />
        ),
        minWidth: 30,
      },
      {
        title: "Event Time",
        dataIndex: "eventTime",
        key: "eventTime",
        render: (data?: string) => {
          if (!data) {
            return null;
          }

          const timestamp = parseCloudAuditTimestamp(data);
          if (Number.isNaN(timestamp)) {
            return <Typography.Text>{data}</Typography.Text>;
          }

          return <FormattedDateTime timestamp={timestamp} />;
        },
        minWidth: 150,
      },
      {
        title: "Action",
        dataIndex: "eventName",
        key: "eventName",
      },
      {
        title: "Source",
        key: "eventSource",
        render: (data) => (
          <>
            <Typography.Text>{data.eventSource}</Typography.Text>
            <div className="sub-text">{data.region}</div>
          </>
        ),
      },
      {
        title: "Identity",
        key: "principal",
        render: (data) => (
          <>
            <Typography.Text>
              {getCloudAuditIdentity(data.principal)}
            </Typography.Text>
            <div className="sub-text">{data.ipAddress}</div>
          </>
        ),
      },
      {
        title: "User Agent",
        dataIndex: "userAgent",
        key: "userAgent",
        render: (data?: string) => formatUserAgent(data),
      },
    ];

    if (showStatus) {
      nextColumns.push({
        title: source === "gcp-cloud-audit" ? "Severity" : "Status",
        dataIndex: "errorCode",
        key: "errorCode",
        render: (data?: string) => {
          const tagColor = data ? statusTagColor(data, source) : undefined;
          return data && tagColor ? (
            <StyledTag color={tagColor}>{data}</StyledTag>
          ) : data ? (
            <Typography.Text>{data}</Typography.Text>
          ) : null;
        },
      });
    }

    return nextColumns;
  }, [handleExpandJson, showStatus, source]);

  const rowKey = useCallback(
    (record: CloudAuditQueryRow, index?: number) =>
      record.eventId ?? `${record.eventTime ?? ""}-${index ?? 0}`,
    []
  );

  const toggleShowStatus = useCallback((e: CheckboxChangeEvent) => {
    setShowStatus(e.target.checked);
  }, []);

  const handleCloseExpandedJson = useCallback(() => {
    setExpandedJson(null);
  }, []);

  const note =
    source === "gcp-cloud-audit"
      ? "The data shown here is based on Google Cloud audit logs and may lag behind recent activity."
      : "The data shown here is based on CloudTrail logs and may not reflect the most recent activity. It may take up to 5 minutes for the logs to be updated.";

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" style={{ width: "100%" }}>
        {update}
        <Checkbox checked={showStatus} onChange={toggleShowStatus}>
          Show Status
        </Checkbox>
      </Flex>
      <Table
        bordered
        columns={columns}
        dataSource={rows}
        rowKey={rowKey}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: false,
          hideOnSinglePage: true,
        }}
        style={{ overflow: "scroll" }}
        locale={{
          emptyText: (
            <Empty
              description="No activity"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
      <JsonViewerModal
        isOpen={!!expandedJson}
        onClose={handleCloseExpandedJson}
        data={expandedJson?.data}
        title={expandedJson?.title || ""}
      />
      <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
        {note}
      </Typography.Text>
    </Flex>
  );
};
